import { allocateByLargestRemainder } from './math';

// Shares-variant author/user config, stored under `formData[fieldId].shares`.
// All counts are whole shares; percent is 0–100; capital is in the node's
// resolved currency. ESOP is form-only (never persisted) per the locked spec.
export interface SharesConfig {
  totalShareCapital?: number;
  authorizedShares?: number;
  esopEnabled?: boolean;
  esopPercent?: number;
  issuedShares?: number;
  pricePerShare?: number;
}

// Membership-variant (LLC) config, stored under `formData[fieldId].membership`.
// Capital is in the node's resolved currency; units are whole membership units.
// No authorized/ESOP/issued split — every unit is issued (authorized = issued).
export interface MembershipConfig {
  totalShareCapital?: number;
  // Author toggle: when on, the author sets an explicit unit count and price
  // derives as capital ÷ units; when off, price = 1 and units = capital.
  hasIssuedUnits?: boolean;
  totalUnits?: number;
  // When on, members may hold the Manager role (≥1 required, M2); when off,
  // every member is locked to Member (M1).
  managerManaged?: boolean;
}

// Partnership-variant config. Capital only — price per unit is always 1, so a
// partner's contribution is their % of the total (no authorized/units/ESOP).
export interface PartnershipConfig {
  totalShareCapital?: number;
}

export interface SharesComputation {
  authorizedShares: number;
  issuedShares: number;
  /** Shares reserved for the option pool (floor(authorized × esop%)); 0 when ESOP off. */
  esopPoolShares: number;
  /** authorized − esopPool − issuedOutstanding (never negative). */
  unissuedReserve: number;
  /** Most shares issuable given the pool reservation: authorized − esopPool. */
  maxIssuable: number;
  /** Σ per-owner shares — the issued shares actually held by owners (= issued when % totals 100). */
  issuedOutstanding: number;
  /** Per-owner whole-share counts = % × issued (largest-remainder so they sum to `issuedOutstanding`). Aligned to `ownerWeights`. */
  perOwnerShares: number[];
  /** Per-owner capital contribution = shares × price. Aligned to `ownerWeights`. */
  perOwnerCapital: number[];
  /** Σ per-owner capital. */
  totalCapital: number;
  /** Effective price per share — derived (share capital ÷ authorized, 6 dp) when share capital is set, else the manual input. */
  pricePerShare: number;
  /** True when price is derived from share capital — the field is then read-only. */
  priceIsDerived: boolean;
  /** Effective total share capital = the explicit input when Share Capital is enabled, else authorized × price. Validated against the jurisdiction minimum. */
  effectiveShareCapital: number;
  /** Σ owner weights (the % column total). */
  percentTotal: number;
  // ── Validation flags (messages live in the component) ──
  /** S1: issued exceeds what the structure allows (authorized − esopPool). */
  exceedsMaxIssuable: boolean;
  /** S3: owner percentages total 100 (vacuously true with no owners). */
  percentBalanced: boolean;
}

const PERCENT_EPSILON = 1e-6;

export function hasZeroPercentOwner(
  ids: readonly string[],
  entries: Record<string, { ownershipPercent?: number } | undefined>
): boolean {
  return ids.some((id) => !((entries[id]?.ownershipPercent ?? 0) > 0));
}

// Derive every downstream Shares number from the raw config + the owners' %
// weights. Pure: same inputs → same output,so table/summary and the
// validations all read from one source.
export function computeShares(
  config: SharesConfig,
  ownerWeights: readonly number[],
  opts?: {
    shareCapitalEnabled?: boolean;
    // Field visibility (author config). Authorized shares and the ESOP pool can be
    // hidden; a hidden field's value is derived from the others rather than read
    // from its (hidden) input. Issued shares and price are always visible.
    authorizedVisible?: boolean;
    esopVisible?: boolean;
  }
): SharesComputation {
  // ESOP only reserves a pool when its field is visible AND enabled with a %.
  // Hidden ESOP (e.g. non-US jurisdictions) is treated as 0% — no pool.
  const esopFraction =
    opts?.esopVisible !== false && config.esopEnabled && config.esopPercent
      ? config.esopPercent / 100
      : 0;

  // Issued is always read from its input. Authorized is read from its input, or
  // derived when hidden: authorized = issued ÷ (1 − esop%) — issued is the
  // issued-outstanding portion and the ESOP reserve makes up the rest; with ESOP
  // also hidden the divisor is 1, so authorized = issued.
  const issuedShares = Math.max(0, config.issuedShares ?? 0);
  const authorizedShares =
    opts?.authorizedVisible === false
      ? esopFraction > 0
        ? Math.round(issuedShares / (1 - esopFraction))
        : issuedShares
      : Math.max(0, config.authorizedShares ?? 0);

  // Price per share = par value = share capital ÷ authorized shares (6 dp), but
  // only when the author enabled the Share Capital input; otherwise price is the
  // manual (editable) input.
  const derivedPrice =
    opts?.shareCapitalEnabled &&
    config.totalShareCapital != null &&
    authorizedShares > 0
      ? Math.round((config.totalShareCapital / authorizedShares) * 1e6) / 1e6
      : undefined;
  const price = derivedPrice ?? config.pricePerShare ?? 0;
  const priceIsDerived = derivedPrice !== undefined;

  const esopPoolShares = Math.floor(authorizedShares * esopFraction);

  const maxIssuable = Math.max(0, authorizedShares - esopPoolShares);

  const percentTotal = ownerWeights.reduce((sum, w) => sum + Math.max(0, w), 0);
  const percentBalanced =
    ownerWeights.length === 0 || Math.abs(percentTotal - 100) < PERCENT_EPSILON;

  // Each owner holds their own % of issued — distribute only the issued portion
  // the percentages actually cover (a lone 30% owner of 60 issued holds 18, not
  // all 60), largest-remainder so the whole-share counts sum exactly.
  const issuedToOwners = Math.round((issuedShares * percentTotal) / 100);
  const perOwnerShares = allocateByLargestRemainder(
    issuedToOwners,
    ownerWeights
  );
  const issuedOutstanding = perOwnerShares.reduce((sum, s) => sum + s, 0);
  const perOwnerCapital = perOwnerShares.map((shares) => shares * price);
  const totalCapital = perOwnerCapital.reduce((sum, c) => sum + c, 0);

  const unissuedReserve = Math.max(
    0,
    authorizedShares - esopPoolShares - issuedOutstanding
  );

  const effectiveShareCapital =
    opts?.shareCapitalEnabled && config.totalShareCapital != null
      ? config.totalShareCapital
      : authorizedShares * price;

  return {
    authorizedShares,
    issuedShares,
    esopPoolShares,
    unissuedReserve,
    maxIssuable,
    issuedOutstanding,
    perOwnerShares,
    perOwnerCapital,
    totalCapital,
    pricePerShare: price,
    priceIsDerived,
    effectiveShareCapital,
    percentTotal,
    exceedsMaxIssuable: issuedShares > maxIssuable,
    percentBalanced,
  };
}

export interface MembershipComputation {
  /** Total membership units — capital (units off, price = 1) or the explicit count (units on). */
  totalUnits: number;
  /** Price per unit — 1 when units off, else capital ÷ units (6 dp). */
  pricePerUnit: number;
  /** Per-member whole-unit counts = % × totalUnits (largest-remainder so they sum exactly). Aligned to `ownerWeights`. */
  perOwnerUnits: number[];
  /** Σ per-member units — the units actually held (= totalUnits when % totals 100). */
  unitsOutstanding: number;
  /** Per-member capital contribution = units × price. Aligned to `ownerWeights`. */
  perOwnerCapital: number[];
  /** Σ per-member capital. */
  totalCapitalAllocated: number;
  /** The configured total share capital (the denominator capital). */
  totalShareCapital: number;
  /** True when the author enabled explicit unit issuance (drives the Units column). */
  hasIssuedUnits: boolean;
  /** True when the company is manager-managed (members may hold the Manager role). */
  managerManaged: boolean;
  /** Σ owner weights (the % column total). */
  percentTotal: number;
  /** M3: member percentages total 100 (vacuously true with no members). */
  percentBalanced: boolean;
}

// Membership mirror of `computeShares`: derive units + per-member capital from
// the raw config + the members' % weights. Pure, single source for table/summary
// and validations. No ESOP / authorized-vs-issued split — every unit is issued.
export function computeMembership(
  config: MembershipConfig,
  ownerWeights: readonly number[]
): MembershipComputation {
  const totalShareCapital = Math.max(0, config.totalShareCapital ?? 0);
  const hasIssuedUnits = !!config.hasIssuedUnits;
  const managerManaged = !!config.managerManaged;

  // Units off: price/unit = 1, so units = capital. Units on: the author sets the
  // unit count and price/unit = capital ÷ units (6 dp). Either way a member's
  // capital = % × total capital. `authorizedShares` is an integer on the backend
  // (StockClassAdditionalInfoDto @IsInt), so the unit count must be whole — floor
  // it so a fractional capital amount (units off) can't produce a non-integer.
  const totalUnits = Math.floor(
    hasIssuedUnits ? Math.max(0, config.totalUnits ?? 0) : totalShareCapital
  );
  const pricePerUnit = hasIssuedUnits
    ? totalUnits > 0
      ? Math.round((totalShareCapital / totalUnits) * 1e6) / 1e6
      : 0
    : 1;

  const percentTotal = ownerWeights.reduce((sum, w) => sum + Math.max(0, w), 0);
  const percentBalanced =
    ownerWeights.length === 0 || Math.abs(percentTotal - 100) < PERCENT_EPSILON;

  // Each member holds their % of total units — distribute only the portion the
  // percentages cover, largest-remainder so whole-unit counts sum exactly.
  const unitsToOwners = Math.round((totalUnits * percentTotal) / 100);
  const perOwnerUnits = allocateByLargestRemainder(unitsToOwners, ownerWeights);
  const unitsOutstanding = perOwnerUnits.reduce((sum, u) => sum + u, 0);
  const perOwnerCapital = perOwnerUnits.map((units) => units * pricePerUnit);
  const totalCapitalAllocated = perOwnerCapital.reduce((sum, c) => sum + c, 0);

  return {
    totalUnits,
    pricePerUnit,
    perOwnerUnits,
    unitsOutstanding,
    perOwnerCapital,
    totalCapitalAllocated,
    totalShareCapital,
    hasIssuedUnits,
    managerManaged,
    percentTotal,
    percentBalanced,
  };
}

export interface PartnershipComputation {
  /** Total partnership capital (the denominator). */
  totalShareCapital: number;
  /** Per-partner capital contribution = % × capital (largest-remainder whole numbers). Aligned to `ownerWeights`. */
  perOwnerCapital: number[];
  /** Σ per-partner capital (= total capital when % totals 100). */
  totalCapitalAllocated: number;
  /** Σ owner weights (the % column total). */
  percentTotal: number;
  /** P3: partner percentages total 100 (vacuously true with no partners). */
  percentBalanced: boolean;
}

// Partnership mirror of computeMembership without units/governance: price per
// unit of capital is 1, so a partner's contribution is their % of the total
// capital. Pure; largest-remainder so whole contributions sum exactly.
export function computePartnership(
  config: PartnershipConfig,
  ownerWeights: readonly number[]
): PartnershipComputation {
  // Capital backs the class at price 1, so it IS the (integer) authorized count.
  // Floor so a fractional amount can't break the backend @IsInt on authorizedShares.
  const totalShareCapital = Math.floor(
    Math.max(0, config.totalShareCapital ?? 0)
  );
  const percentTotal = ownerWeights.reduce((sum, w) => sum + Math.max(0, w), 0);
  const percentBalanced =
    ownerWeights.length === 0 || Math.abs(percentTotal - 100) < PERCENT_EPSILON;

  const capitalToOwners = Math.round((totalShareCapital * percentTotal) / 100);
  const perOwnerCapital = allocateByLargestRemainder(
    capitalToOwners,
    ownerWeights
  );
  const totalCapitalAllocated = perOwnerCapital.reduce((sum, c) => sum + c, 0);

  return {
    totalShareCapital,
    perOwnerCapital,
    totalCapitalAllocated,
    percentTotal,
    percentBalanced,
  };
}
